import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import BackgroundImage from "../../assets/background.png";
import Auth_API from "utilities/api/auth_api";
import { getUserSuccess } from "../../redux/slices/userSlice";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Logos from "components/logos";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { InputAdornment, IconButton } from "@mui/material";

const boxStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "0.75rem",
  alignSelf: "stretch",
};

const typographyStyle = {
  color: "#5C5C5C",
  textAlign: "center",
  fontFamily: "Inter",
  fontSize: "24px",
  fontWeight: 400,
  lineHeight: "normal",
  letterSpacing: "-0.01375rem",
};

// TextField custom styles
const styledTextfield = {
  display: "flex",
  gap: "0.2rem",
  alignSelf: "stretch",
  border: "1px solid #B5B5B5",
  borderRadius: "12px",
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: "none",
    },
  },
  "& .MuiInputBase-root": {
    borderRadius: "12px",
    "& input": {
      padding: "0.6rem 1rem",
    },
  },
};

// Form validation schema
const validation_schema = Yup.object({
  email: Yup.string().email("Invalid email address").required("Required"),

});

/**
 * StudentLoginPage Component
 * Handles student authentication with email/password
 * Supports first-time login password change flow
 */
const StudentLoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const passwordRef = React.useRef();
  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  // Redirect to admin site
  const handleRedirect = () => {
    window.location.href = process.env.REACT_APP_ADMIN_SITE_BASE_URL;
  };

  // Formik form handler
  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: validation_schema,
  onSubmit: (values) => {
  const { email } = values;
  const password = passwordRef.current?.value || "";

  // Basic password check
  if (!password) {
    toast.error("Password is required");
    return;
  }

  Auth_API.studentLogin({ email, password })
    .then((response) => {
      if (response && response.data.password_change_required) {
        toast.info(
          "Password change required for first login. Redirecting to reset password page..."
        );
        navigate("/reset_password", {
          state: {
            email: email,
            user_id: response.data.user_id,
            isFirstLogin: true,
          },
        });
        return;
      }

      dispatch(getUserSuccess(response.data));
      toast.success("Login successful!");
      navigate("/dashboard");
    })
    .catch((error) => {
      console.error("Login error:", error);
      toast.error("Login failed. Please check your credentials.");
    })
    .finally(() => {
      // Clear password input for security
      if (passwordRef.current) passwordRef.current.value = "";
    });
},

  });

  return (
    <Box sx={{ display: "flex", position: "relative", minHeight: "100vh" }}>
      <Box
        sx={{
          fontFamily: "Roboto",
          width: "100vw",
          height: "100vh",
          overflowY: "auto",
          boxSizing: "border-box",
          backgroundImage: `url(${BackgroundImage})`,
          backgroundSize: "cover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignSelf: "flex-end",
            marginTop: "2rem",
            marginRight: "2rem",
          }}
        >
          <Typography
            onClick={handleRedirect}
            sx={{
              textDecoration: "underline",
              color: "#0062CC",
              cursor: "pointer",
            }}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") handleRedirect();
            }}
          >
            Go to Admin site
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1.5rem",
            padding: { xs: "1rem", sm: "2rem" },
            borderRadius: "1rem",
            backgroundColor: "#FFF",
            width: { xs: "90%", sm: "80%", md: "40%", lg: "25.2rem" },
            boxShadow: "4px 4px 44px 0px #0000001A",
            margin: "auto",
          }}
        >
          {/* Login Form */}
          <Box
            component="form"
            onSubmit={formik.handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1.5rem",
              width: "100%",
            }}
          >
            <Box sx={boxStyle}>
              <Typography sx={typographyStyle}>Email Address</Typography>
              <TextField
                id="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                sx={styledTextfield}
                placeholder="Enter email address"
              />
            </Box>
            <Box sx={boxStyle}>
              <Typography sx={typographyStyle}>Password</Typography>
            <TextField
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}   
            inputRef={passwordRef}
            sx={styledTextfield}
            placeholder="Enter password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: '#666' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
    ),
  }}
/>

            </Box>
            <Typography
              sx={{
                ...typographyStyle,
                fontSize: "20px",
                fontweight: 500,
                letterSpacing: "-0.01375rem",
                alignSelf: "flex-end",
                cursor: "pointer",
                color: "#007BFF",
              }}
              onClick={() => navigate("/forgot_password")}
            >
              Forgot your password?
            </Typography>

            <Button
              type="submit"
              sx={{
                display: "flex",
                padding: "1rem",
                justifyContent: "center",
                alignItems: "center",
                gap: "0.5rem",
                alignSelf: "stretch",
                borderRadius: "0.75rem",
                backgroundColor: "#B2D7FF",
                color: "#212121",
                transition: "background-color 0.3s",
                "&:hover": {
                  backgroundColor: "#1f62c9", // Darker shade for hover
                },
                "&:active": {
                  backgroundColor: "#8DAFDD", // Even darker shade for active state
                },
              }}
            >
              <Typography
                sx={{
                  textAlign: "center",
                  fontFamily: "Inter",
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  lineHeight: "normal",
                  letterSpacing: "-0.01375rem",
                  textTransform: "none", // To prevent all caps
                }}
              >
                Login{" "}
              </Typography>
            </Button>

            {/* Disclaimer text added below login button */}
            <Typography
              sx={{
                color: "#212121",
                textAlign: "center",
                fontFamily: "Inter",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "normal",
                marginTop: "1rem",
              }}
            >
              <b>
                By using the MBF MIS, you consent to the processing of your
                contact information by Maker Bhavan Foundation for marketing &
                communication purposes
              </b>
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between", // Distributes space between items
              width: "100%",
              alignItems: "center", // Aligns items vertically
              padding: 2, // Optional: adds padding to the Box
            }}
          >
            <Typography
              component="span"
              onClick={() => navigate(-1)} // Navigate back
              sx={{
                display: "flex",
                alignItems: "center",
                color: "#007BFF",
                cursor: "pointer",
                fontSize: "18px",
                fontWeight: 400,
                lineHeight: "normal",
                letterSpacing: "-0.01375rem",
              }}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") navigate(-1);
              }}
            >
              <ArrowBackIcon sx={{ marginRight: 0.5 }} />
              Back
            </Typography>
            <Typography sx={{ fontSize: "18px" }}>
              Don’t have an account?
              <Typography
                component="span"
                onClick={() => navigate("/register-instructions")}
                sx={{
                  ml: 1,
                  color: "#007BFF",
                  cursor: "pointer",
                  fontSize: "18px",
                  fontWeight: 400,
                  lineHeight: "normal",
                  letterSpacing: "-0.01375rem",
                }}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    navigate("/register-instructions");
                }}
              >
                Register here
              </Typography>
            </Typography>
          </Box>
        </Box>
        <Logos />
      </Box>

      {/* Snackbar Notification */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentLoginPage;
